/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/**
 * GlobalsUtils - Class for handling global values.
 */
import { normalize } from '@intl/adobe-locales';
import type { AutoCompleteOptions, HostContext, imsData } from '../@types';
import PolyfillUtils from '../utils/PolyfillUtils';
import Trie from './Autocomplete/Trie';
export const DataStatus = Object.freeze({
	EMPTY: Symbol('empty'),
	LOADING: Symbol('loading'),
	ERROR: Symbol('error'),
	HAVE_DATA: Symbol('have_data'),
});
export default class GlobalsUtils {
	static imsData: imsData;
	static dataStatus: (...args: Array<any>) => any;
	static locale: string;
	static apiKey: string;
	static assetsRequestBody: Record<string, any>;
	static fetchAssetsRequestCount: number;
	static isPolling: boolean;
	static sentViewLog: boolean;
	static sentRenditionLog: boolean;
	static sentMimetypeLog: boolean;
	static sentLinksServiceLog: boolean;
	static pollingInterval: number;
	static pollingTimerIDs: Array<number>;
	static shouldRender: boolean;
	static isEmptyView: boolean;
	static clientData: Record<string, any>;
	static srpScrollPositionY: number;

	/**
	 * Method to ensure we have a string
	 * @param {String} text
	 */
	static isString(text: string) {
		return typeof text === 'string' && text !== 'null' && text !== 'undefined';
	}

	/**
	 * Method to ensure we have an Array
	 * @param {Array}
	 */
	static isArray(array: Array<any>) {
		return array && Array.isArray(array);
	}

	/**
	 * Method to ensure we have a Function
	 * @param {Function}
	 */
	static isFunction(func: (...args: Array<any>) => any) {
		return func && typeof func === 'function';
	}

	/**
	 * Method to return host's api key
	 */
	static getAPIKey() {
		return GlobalsUtils.apiKey;
	}

	/**
	 * Method to check and execute Promise allSettled polyfill
	 */
	static checkAndExecutePromiseAllSettledPolyfill(promises) {
		if (Promise && !Promise.allSettled) {
			return PolyfillUtils.promisesAllSettled(promises);
		}
		return Promise.allSettled(promises);
	}

	/**
	 * Process string to remove dots from end of string
	 */
	static removePeriodFromEnd(s = ''): string {
		return s.replace(/\.+$/, '');
	}

	static removeExtensionFromString(s = ''): string {
		const lastPeriodIndex = s.lastIndexOf('.');
		if (lastPeriodIndex !== -1) {
			return s.substring(0, lastPeriodIndex);
		}
		return s;
	}

	static createDictionary(words: string[], hostContext: HostContext): Trie {
		const dictionary = new Trie();
		const insertedWords = [];
		const nonInsertedWords = [];
		for (const word of words) {
			const inserted: boolean = dictionary.insert(word, {
				ignoreCase: true,
				locale: normalize(hostContext.locale),
			});
			inserted ? insertedWords.push(word) : nonInsertedWords.push(word);
		}
		return dictionary;
	}

	static autoComplete(dictionary: Trie, word: string, options: AutoCompleteOptions): string[] {
		return dictionary?.autoComplete(word, options) ?? [];
	}
}
GlobalsUtils.isEmptyView = false;
GlobalsUtils.assetsRequestBody = {};
GlobalsUtils.fetchAssetsRequestCount = 0;
GlobalsUtils.isPolling = false;
GlobalsUtils.sentViewLog = false;
GlobalsUtils.sentRenditionLog = false;
GlobalsUtils.sentMimetypeLog = false;
GlobalsUtils.sentLinksServiceLog = false;
GlobalsUtils.pollingInterval = 0;
GlobalsUtils.pollingTimerIDs = [];
GlobalsUtils.shouldRender = true;
GlobalsUtils.clientData = {};
GlobalsUtils.srpScrollPositionY = 0;
